import React from 'react'
import Layout from '@components/Layout'
import { graphql } from 'gatsby'
import ProfessionalDetailContent from '@sections/ProfessionalDetailContent'
import { getHrefLangUrls, handleSEO } from '@utils'
import TitleAndMetaTags from '@components/TitleAndMetaTags'
import { useGtag, useLocale } from '@hooks'

const ProfessionalDetail = ({
  data: { professional, events, news, insights, parentPath, accolades },
  pageContext,
  location,
}) => {
  parentPath = parentPath?.slug

  const previousQuery = location?.state?.query
  const previousRefinements = location?.state?.refinementList

  const { getLocalizedSlug } = useLocale()

  useGtag({
    action: 'Professionals',
    categories: professional?.practiceAreas?.map(({ title }) => title),
    label: getLocalizedSlug({
      slug: professional?.slug,
      contentType: professional?.__typename,
    }),
  })

  return (
    <Layout {...pageContext} contentType="professional">
      <ProfessionalDetailContent
        previousQuery={previousQuery}
        previousRefinements={previousRefinements}
        professional={professional}
        events={events?.nodes}
        news={news?.nodes}
        insights={insights?.nodes}
        newAccolades={accolades?.nodes}
        parentPath={parentPath}
        location={location}
      />
    </Layout>
  )
}

export const query = graphql`
  query ProfessionalDetailV2(
    $contentful_id: String!
    $node_locale: String!
    $displayOnSitesAsNodeLocales: [String!]
  ) {
    professional: contentfulProfessional(
      contentful_id: { eq: $contentful_id }
      node_locale: { eq: $node_locale }
    ) {
      ...baseProfessionalFields
      company
      overrideCompany
      seo {
        ...seoMetadata
      }
      speakingEngagementsHtml: childContentfulProfessionalSpeakingEngagementsHtmlJsonNode {
        html
      }
      involvementHtml: childContentfulProfessionalInvolvementHtmlJsonNode {
        html
      }
      recentRepresentationsHtml: childContentfulProfessionalRecentRepresentationsHtmlJsonNode {
        html
      }
      overviewHtml: childContentfulProfessionalBioHtmlJsonNode {
        html
      }
      bioAccoladesHtml: childContentfulProfessionalBioAccoladesHtmlJsonNode {
        html
      }
      bioEducationHtml: childContentfulProfessionalBioEducationHtmlJsonNode {
        html
      }
      languages {
        id: contentful_id
        title
      }
      image {
        ...assetBase
        # gatsbyImageData(width: 320)
      }
      practiceAreas {
        ...basePracticeAreaFields
      }
      education {
        id: contentful_id
        schoolName
        degree
        year
      }
      admissions {
        title
        id: contentful_id
      }
      offices {
        ...officeCardFields
      }
      links {
        type
        url
        link
      }
    }
    events: allContentfulEvent(
      sort: { eventStart: DESC }
      filter: {
        associatedProfessionals: {
          elemMatch: { contentful_id: { eq: $contentful_id } }
        }
        node_locale: { eq: $node_locale }
      }
    ) {
      nodes {
        ...baseEventFields
      }
    }
    insights: allContentfulArticle(
      sort: [{ publicationDate: DESC }, { createdAt: ASC }, { title: ASC }]
      filter: {
        associatedProfessionals: {
          elemMatch: { contentful_id: { eq: $contentful_id } }
        }
        node_locale: { eq: $node_locale }
      }
    ) {
      nodes {
        ...baseArticleFields
      }
    }
    accolades: allContentfulAccolade(
      sort: [{ publicationDate: DESC }, { createdAt: ASC }, { title: ASC }]
      filter: {
        associatedProfessionals: {
          elemMatch: { contentful_id: { eq: $contentful_id } }
        }
        node_locale: { eq: $node_locale }
      }
    ) {
      nodes {
        ...baseAccoladeFields
      }
    }
    news: allContentfulNews(
      sort: [{ publicationDate: DESC }, { createdAt: ASC }, { title: ASC }]
      filter: {
        associatedProfessionals: {
          elemMatch: { contentful_id: { eq: $contentful_id } }
        }
        node_locale: { eq: $node_locale }
      }
    ) {
      nodes {
        ...baseNewsFields
      }
    }
    parentPath: contentfulPage(
      contentful_id: { eq: "1WMCfztCvBPa3ywF0FjyRi" }
      node_locale: { eq: $node_locale }
    ) {
      slug
      node_locale
    }
    currentPageWithAllLocales: allContentfulProfessional(
      filter: {
        node_locale: { in: $displayOnSitesAsNodeLocales }
        contentful_id: { eq: $contentful_id }
      }
    ) {
      nodes {
        slug
        node_locale
      }
    }
  }
`

export default ProfessionalDetail

export const Head = ({
  data: { professional, currentPageWithAllLocales },
  pageContext: { displayLocale, parentPages },
}) => {
  const { name, overviewHtml, practiceAreas, seo } = professional

  const { seoTitle, seoDescription, seoImage } = handleSEO(
    seo,
    {
      name,
      overviewHtml: overviewHtml?.html,
      practiceAreas: practiceAreas,
    },
    displayLocale,
    'professional'
  )

  const hrefLangUrls = getHrefLangUrls(
    currentPageWithAllLocales?.nodes,
    parentPages
  )

  const canonicalSlug = `/professionals/${professional?.slug}`

  return (
    <TitleAndMetaTags
      hrefLangUrls={hrefLangUrls}
      title={seoTitle}
      image={seoImage}
      description={seoDescription}
      // keywords={seoKeywords}
      canonicalSlug={canonicalSlug}
      usDefault={seo?.enUsCanonicalUrl}
      noIndex={seo?.noIndex}
      noArchive={seo?.noArchive}
    />
  )
}
